import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { forgetSessionExpiredLanguage, rememberSignedInTab, sessionExpiredLanguage } from '@/lib/return-path';
import { isSupportedLanguage, type LanguageCode } from '@/config/languages';
import i18n, { preloadLanguage } from './index';
import { bcp47For, resolveLocale } from './negotiate';
import { browserEligible } from './browser-eligible';
import { LocaleContext, isEnglishOnlyRole, type LocaleContextValue } from './useLocale';

/**
 * #2507: while set on `<html>`, `index.css` hides `#root` behind a spinner.
 * Set while a negotiated non-English language has not arrived yet, so the
 * first paint is never the English shell.
 */
export const LOCALE_HOLD_ATTR = 'data-locale-hold';

/**
 * #2507: the longest the page stays hidden for a language. After it, the page
 * shows with whatever has arrived: a stalled chunk must never blank the app.
 */
export const LOCALE_HOLD_CAP_MS = 3000;

/** The anonymous visitor’s choice; a signed-in user’s lives on the profile. */
export const LOCAL_STORAGE_KEY = 'trf.lang';

function readLocalStorage(): string | null {
  try {
    return window.localStorage.getItem(LOCAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(code: string): void {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, code);
  } catch {
    // Private window, blocked site data: the choice lives for this render only.
  }
}

/**
 * The language a `?lang=` link brought this tab (#2604). The query names only
 * the arrival page; the redirect or link that follows drops it, so the visit
 * fell back to English. Kept in this tab's `sessionStorage` like the
 * session-ended marker (`sessionExpiredLanguage`): a shared device keeps each
 * tab's own `trf.lang`, and nothing reaches another tab or the next browser
 * session. Read while signed out only — a signed-in person's profile governs.
 * A choice on the switcher replaces it (`setLocale`).
 */
export const SESSION_STORAGE_KEY = 'trf.lang';

function readSessionStorage(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSessionStorage(code: string): void {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, code);
  } catch {
    // Private window, blocked site data: the link's language lasts this page only.
  }
}

function forgetSessionStorage(): void {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // storage unavailable: nothing to remove
  }
}

interface Props {
  children: ReactNode;
  /**
   * Persists a signed-in user’s choice to the profile (L4 wires the
   * `preferences.language` PATCH). Absent, a signed-in choice applies for the
   * session only. Anonymous choices always go to `localStorage`.
   */
  onPersist?: (code: LanguageCode) => Promise<void> | void;
  /**
   * #2753: saves to the profile a language that reached the session from
   * BEFORE sign-in (a pick on the sign-in page, or a `?lang=` link's
   * language when the profile has none). Called at most once per account and
   * language; a failure is swallowed, the language still holds for the visit.
   */
  onSignInLanguage?: (code: LanguageCode) => Promise<void> | void;
}

/**
 * Negotiates the UI locale (#2109 R1 lane L2), applies it to i18next and
 * writes `<html lang dir>` on every change. Sits inside the router (it reads
 * `?lang=`) and inside `AuthProvider` (it reads `preferences.language`).
 * `navigator.languages` is consulted on a first ANONYMOUS visit only — when
 * neither the URL nor `localStorage` resolved — and is never written anywhere
 * (research §2.2 rule 7). A signed-in user with no `preferences.language`
 * gets `en`, never the browser hint.
 *
 * A choice made through `setLocale` also drops `?lang=` from the current URL
 * (replace, every other param and the hash kept): the query param is a
 * link's suggestion for the arrival, and once the person has chosen it must
 * not outrank that choice on the next resolution (#2109 R1 walk defect).
 * Fresh navigations keep the precedence URL → stored → hint → en.
 *
 * Staff are English-only (DECISIONS 2026-09-30). While the active role is
 * TRFer or admin the locale is `en` whatever the URL, the profile or an
 * in-session choice says; nothing is written, so the saved
 * `preferences.language` and any in-session choice come back the moment the
 * role switches to Parent. `setLocale` is a no-op for staff for the same
 * reason — a staff seat never overwrites the person's family-side language.
 */
export function LocaleProvider({ children, onPersist, onSignInLanguage }: Props) {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chosen, setChosen] = useState<LanguageCode | null>(null);

  // #2335: an in-session choice belongs to the person who made it. When the
  // signed-in account changes — sign-out, sign-in, or one account replacing
  // another on the same page — the choice is dropped, so the next person's
  // language comes from their own profile (signed in) or `trf.lang`
  // (anonymous). Keyed on the account, not the role: a staff seat switching
  // back to Parent keeps the choice (the staff rule above). Reset during
  // render, so no frame paints the previous person's language.
  const accountKey = user ? (user.id ?? user._id ?? user.email) : null;
  //
  // #2753: ONE exception — a choice made while nobody was signed in (the
  // sign-in page's own switcher) is made by the person who is about to sign
  // in, so it crosses that one boundary. It then outranks the profile's saved
  // language and is saved to the profile (`signInPick` → `onSignInLanguage`).
  // Any other change of account still drops it.
  const [chosenBy, setChosenBy] = useState<string | null>(accountKey);
  const [signInPick, setSignInPick] = useState<LanguageCode | null>(null);
  if (chosenBy !== accountKey) {
    setChosenBy(accountKey);
    if (chosen !== null && chosenBy === null && accountKey !== null) {
      setSignInPick(chosen);
    } else {
      if (chosen !== null) setChosen(null);
      if (signInPick !== null) setSignInPick(null);
    }
  }

  const query = useMemo(() => new URLSearchParams(search).get('lang'), [search]);
  const preferences = (user as { preferences?: { language?: string } } | null)?.preferences;
  const profileLanguage = preferences?.language ?? null;
  // #2881: every account is created with `preferences.language: 'en'`, so a
  // saved English is the default, not a choice; any other saved language is
  // the person's own and a link never replaces it. A user without
  // `preferences` is one whose profile has not loaded yet, not an empty one.
  const savedChoice = profileLanguage && profileLanguage !== 'en' ? profileLanguage : null;
  // `chosen` is ignored on the render that detects an account change (the
  // reset above lands on the re-render React runs before committing).
  const current = chosenBy === accountKey ? chosen : null;
  // #2604 / #2753: the `?lang=` link's language, read whoever is signed in.
  // Signed out it ranks as before (below); signed in it only fills in a
  // profile that has no language of its own.
  const arrivalLink = readSessionStorage();
  // #2429 follow-up: while signed out, a session that ended in this tab in the
  // last 30 minutes names her language (tab-scoped `sessionStorage`), so a
  // hand-typed /login with no `?lang=` still reads in it. It outranks
  // `trf.lang`, which may be another person's choice on a shared device.
  const expiredLanguage = user ? null : sessionExpiredLanguage();
  // #2604: the language a `?lang=` link brought this tab, for the pages that
  // follow without the query. Below the expiry marker (a session that ended
  // here is the more recent fact), above `trf.lang` in `localStorage`.
  const linkLanguage = user ? null : arrivalLink;
  const stored =
    current ??
    (user
      ? (savedChoice ?? (isSupportedLanguage(arrivalLink) ? arrivalLink : profileLanguage))
      : (expiredLanguage ?? linkLanguage ?? readLocalStorage()));

  // #2604: remember a supported `?lang=` for this tab. Written whoever is
  // signed in, so the value is the same on the cold boot's anonymous render
  // and after the session check lands; it is only ever READ while signed out.
  useEffect(() => {
    if (isSupportedLanguage(query)) writeSessionStorage(query);
  }, [query]);

  const staffEnglishOnly = isEnglishOnlyRole((user as { role?: string } | null)?.role);

  // #2753: the language to save to the profile, if any. The person's own pick
  // on the sign-in page, else the link's language where the loaded profile
  // holds only the default (#2881). A staff seat saves nothing (its profile
  // language is the family side's).
  const adoptable: LanguageCode | null =
    signInPick ?? (preferences && !savedChoice && isSupportedLanguage(arrivalLink) ? arrivalLink : null);
  const adoptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || staffEnglishOnly || !onSignInLanguage || !adoptable || adoptable === profileLanguage) return;
    const key = `${accountKey}:${adoptable}`;
    if (adoptedRef.current === key) return;
    adoptedRef.current = key;
    try {
      Promise.resolve(onSignInLanguage(adoptable)).catch(() => {
        // The language still holds for this visit; the next sign-in asks again.
      });
    } catch {
      // A synchronous failure is the same: nothing to undo.
    }
  }, [user, staffEnglishOnly, onSignInLanguage, adoptable, profileLanguage, accountKey]);

  // #2753: a `?lang=` link's language belongs to the person it arrived for.
  // Once an account leaves this tab it is stale, so the next person to sign in
  // on a shared device does not inherit it.
  const previousAccount = useRef<string | null>(null);
  useEffect(() => {
    if (previousAccount.current !== null && accountKey === null) forgetSessionStorage();
    previousAccount.current = accountKey == null ? null : String(accountKey);
  }, [accountKey]);

  const negotiated = useMemo(
    () =>
      resolveLocale({
        query,
        stored,
        navigatorLanguages: user || typeof navigator === 'undefined' ? null : navigator.languages,
        browserEligible: browserEligible(),
      }),
    [query, stored, user],
  );
  const locale: LanguageCode = staffEnglishOnly ? 'en' : negotiated;
  const bcp47 = bcp47For(locale);

  // #2507: the language i18next actually renders, which trails `locale`
  // until the shell namespaces have arrived.
  const [active, setActive] = useState(() => i18n.language);
  useEffect(() => {
    const onChanged = (lng: string) => setActive(lng);
    i18n.on('languageChanged', onChanged);
    setActive(i18n.language);
    return () => i18n.off('languageChanged', onChanged);
  }, []);

  useEffect(() => {
    if (i18n.language !== locale) {
      // #2507: every namespace starts downloading now; the switch itself
      // waits only for the shell ones (`i18nInitOptions`).
      preloadLanguage(locale);
      void i18n.changeLanguage(locale);
    }
    document.documentElement.lang = bcp47;
    document.documentElement.dir = 'ltr';
  }, [locale, bcp47]);

  // #2507: hold the page while a negotiated language is on its way, so a
  // translated cold load (a `?lang=` link, `trf.lang`, a profile language that
  // arrives with the session) never paints English first. Not for a choice
  // made on the switcher: that person is looking at the page and sees it
  // change. Capped, and once the cap has run out it never holds again.
  const [holdSpent, setHoldSpent] = useState(false);
  const holding = locale !== 'en' && active !== locale && current === null && !holdSpent;
  useEffect(() => {
    if (!holding) return;
    const timer = setTimeout(() => setHoldSpent(true), LOCALE_HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [holding]);
  // Layout effect: set before the browser paints the first frame.
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute(LOCALE_HOLD_ATTR, holding);
    return () => document.documentElement.removeAttribute(LOCALE_HOLD_ATTR);
  }, [holding]);

  // #2429 round 3: while signed in, this tab keeps who it holds and the
  // language it shows in its own `sessionStorage`, updated on every language
  // change. A reload after the session ended in another browser reads it to
  // say so on /login in her language (`markSignedInTabEnded` in AuthContext's
  // boot check). Staff seats are English-only, so they record no language.
  // A deliberate sign-out removes the record (`clearReturnPath`).
  const tabLanguage = staffEnglishOnly || locale === 'en' ? null : locale;
  const signedInAccount = accountKey == null ? null : String(accountKey);
  useEffect(() => {
    if (user) rememberSignedInTab({ account: signedInAccount, lang: tabLanguage });
  }, [user, signedInAccount, tabLanguage]);

  const setLocale = useCallback(
    async (code: LanguageCode) => {
      if (staffEnglishOnly) return;
      setChosen(code);
      // #2753: a new choice replaces whatever was carried across sign-in.
      setSignInPick(null);
      const params = new URLSearchParams(search);
      if (params.has('lang')) {
        params.delete('lang');
        const rest = params.toString();
        navigate({ pathname, search: rest ? `?${rest}` : '', hash }, { replace: true });
      }
      // #2604: the link's language was a suggestion for the arrival; the
      // choice governs the rest of the visit (signed in or not, #2753).
      forgetSessionStorage();
      if (user) {
        await onPersist?.(code);
      } else {
        writeLocalStorage(code);
        forgetSessionExpiredLanguage();
      }
    },
    [staffEnglishOnly, user, onPersist, pathname, search, hash, navigate],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, bcp47, setLocale, isEnglish: locale === 'en', staffEnglishOnly }),
    [locale, bcp47, setLocale, staffEnglishOnly],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
